import React from "react"
import styled from "styled-components"
import {
  curry,
  find,
  anyPass,
  path,
  pathOr,
  compose,
  isNil,
  isEmpty,
  includes,
  allPass,
  map,
  prop,
  any,
} from "ramda"
import { graphql } from "@apollo/client/react/hoc"
import { lightgrey } from "../../assets/colors"

export const pgaTld =
  window.location.origin.includes(".sandboxpga.org") ||
  window.location.origin.includes("localhost")
    ? "sandboxpga.org"
    : "pga.org"
export const appserverTld =
  window.location.origin.includes(".sandboxpga.org") ||
  window.location.origin.includes("localhost")
    ? "apps.dev.pga.org"
    : "apps.pga.org"
export const pgaComTld =
  window.location.origin.includes(".sandboxpga.org") ||
  window.location.origin.includes("localhost")
    ? "my.dev.pga.com"
    : "my.pga.com"

export const Separator = styled.div`
  ${props =>
    (props.vertical && "border-right") ||
    (props.horizontal && "border-bottom")}: 2px solid ${lightgrey};
`

export const findObject = curry((arr, label) =>
  find(val => val.label === label)(arr),
)

export const getMeData = (query, context) =>
  compose(
    graphql(query, {
      options: {
        fetchPolicy: "no-cache",
        context,
      },
      props: ({ data }) => ({
        data,
        loading: data.loading,
        me: data.me,
        error: data.error,
        refetch: data.refetch,
      }),
    }),
  )
const MEMBER_TYPE = "MB"
const PUBLIC_EMPLOYER = "PE"
const STUDENT_TYPE = "ST"
const ASSOCIATE_TYPE = "AP"
const PENDING_ASSOCIATE_TYPE = "PA"
const PENDING_AFFILIATE_TYPE = "PF"
const TERMINATED_APPRENTICE = "TA"
const QUALIFIER_ELIGIBLE = "QE"
const PRE_QUALIFIER = "PQ"
const B25_CLASS = "B25"
const AFFILIATE_CLASS = "AF"
const SECTION_STAFF = "SECTION_STAFF"
const HONORARY_MEMBER = "HM"
const A3_CLASS = "A3"

// Checks for coach-tools API field name with fallback to v3 API field name
export const getProgramTypeCode = user =>
  user?.mainProgramTypeCode || user?.type
export const getClassificationCode = user =>
  user?.classificationCode || user?.class

export const isMember = user => getProgramTypeCode(user) === MEMBER_TYPE
export const isPublicEmployer = user =>
  getProgramTypeCode(user) === PUBLIC_EMPLOYER
export const isStudent = user => getProgramTypeCode(user) === STUDENT_TYPE
export const isPendingAffiliate = user => {
  if (!user) return false
  if (user.registeredAffiliate) return false
  if (user.pendingAffiliate) return true

  return getProgramTypeCode(user) === PENDING_AFFILIATE_TYPE
}
export const isRegisteredAffiliate = user => {
  if (!user) return false
  if (isPendingAffiliate(user)) return false
  if (user.registeredAffiliate) return true

  return (
    getClassificationCode(user) === AFFILIATE_CLASS &&
    getProgramTypeCode(user) !== PENDING_AFFILIATE_TYPE
  )
}
// Registered Affiliate only — PF publish uses rate/class AF but is not full Affiliate.
export const isAffiliate = user => isRegisteredAffiliate(user)
export const isPendingAssociate = user =>
  getProgramTypeCode(user) === PENDING_ASSOCIATE_TYPE
export const isAssociate = user => getProgramTypeCode(user) === ASSOCIATE_TYPE
// Account membership shell / finance / Journey eligibility for AP or unpaid PA
export const isAssociateTrack = user =>
  isAssociate(user) || isPendingAssociate(user)
// Account finance shell for registered or unpaid-pending Affiliate (PF).
export const isAffiliateTrack = user =>
  isRegisteredAffiliate(user) || isPendingAffiliate(user)
export const isTerminatedApprentice = user =>
  getProgramTypeCode(user) === TERMINATED_APPRENTICE
export const isPreQualifier = user => getProgramTypeCode(user) === PRE_QUALIFIER
export const isQualifierEligible = user =>
  getProgramTypeCode(user) === QUALIFIER_ELIGIBLE
export const isTerminated = user =>
  path(["termination", "status"])(user) === "Terminated"
export const isSuspended = user =>
  user.suspended || path(["termination", "status"])(user) === "Suspended"
export const isGptpSuspended = user => user.gptpSuspension
export const hasReciprocityAttribute = me =>
  prop("reciprocityEligible", me) === true
export const hasStartedReciprocityLevels = me => {
  const levels = pathOr([], ["education", "levels"], me)
  const reciprocityLevelRegex = /Z.*\d/i
  return any(level => reciprocityLevelRegex.test(level.levelCd || ""), levels)
}
export const isStaff = ({ primaryEmail }) =>
  (primaryEmail || "").endsWith("@pgahq.com")
export const isSectionStaff = user => user.role === SECTION_STAFF
export const isB25Class = user => getClassificationCode(user) === B25_CLASS
export const hasStaffAccess = anyPass([isStaff, isSectionStaff])
export const isTerminatedStaff = allPass([hasStaffAccess, isTerminated])
export const isProspectPath = currentMember =>
  !!currentMember?.prospect?.startedAt
export const shouldSeeProspectDashboard = (me, currentMember) =>
  !isPendingAssociate(me) &&
  (isPendingAffiliate(me) ||
    hasProspectDashboard(me) ||
    (isProspectPath(currentMember) && !hasFinishedQualifying(me)))

// Affiliate→Associate pending: active affiliate_registration band + pending_associate.
// Prefer Membership GraphQL predicates over classificationCode (PA publish overwrites class).
export const isAffiliateToAssociatePending = person =>
  Boolean(person?.pendingAssociate && person?.registeredAffiliate)

export const getAnnouncementGroup = user => {
  if (!user) return undefined
  if (isAffiliateToAssociatePending(user) || isRegisteredAffiliate(user)) {
    return ASSOCIATE_TYPE
  }

  const code = getProgramTypeCode(user)
  return code === "AF" ? ASSOCIATE_TYPE : code
}

export const shouldSeeMembershipJourneyDashboard = person => {
  if (!person) return false
  // Keep Affiliate shell until L1 pay expires the AF band and promotes PA → RG.
  if (isAffiliateToAssociatePending(person)) return false
  if (isPendingAffiliate(person)) return false

  return (
    isAssociateTrack(person) &&
    !isAffiliate(person) &&
    person.curriculumVersion === "3.1"
  )
}
export const shouldShowAnnualSurvey = user =>
  !includes(getClassificationCode(user), [A3_CLASS, HONORARY_MEMBER])

export const hasMembershipType = anyPass([
  isMember,
  isStudent,
  isAssociate,
  isPendingAssociate,
  isPendingAffiliate,
  isAffiliate,
  isQualifierEligible,
])

export const shouldSeeAnnouncements = anyPass([
  isMember,
  isStudent,
  isAssociate,
  isPendingAssociate,
  isAffiliate,
])

export const hasFinishedQualifying = anyPass([
  isMember,
  isStudent,
  isAssociate,
  isAffiliate,
])

export const canAccessCareerProfile = user => Boolean(user)

export const hasProspectDashboard = anyPass([
  isPreQualifier,
  isQualifierEligible,
])

export const shouldSeeJobRecommendations = anyPass([
  hasMembershipType,
  isPreQualifier,
])

export const hasLevelCd = levelCd =>
  compose(
    any(includes(levelCd)),
    map(prop("levelCd")),
    pathOr([], ["education", "levels"]),
  )

export const shouldNotSeeGeneric = anyPass([
  isMember,
  isStudent,
  isAssociate,
  isPendingAssociate,
  isPendingAffiliate,
  isAffiliate,
  isPreQualifier,
  allPass([isQualifierEligible, hasLevelCd("Z")]),
  allPass([isQualifierEligible, hasLevelCd("A")]),
  hasReciprocityAttribute,
])

export const isNilOrEmpty = el => isNil(el) || isEmpty(el)

export const arrayNotEmpty = arr => Array.isArray(arr) && arr.length > 0

// HoC that will allow you to wrap a component in some configuration
// Allows you to set the prop name to something other than 'config', so you can compose this multiple times
export const withConfig =
  (config, propName = "config") =>
  WrappedComponent =>
  props => <WrappedComponent {...props} {...{ [propName]: config }} />
